import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../db/engine';
import { X } from 'lucide-react';
import { fmtFecha } from '../utils/fecha';
import { abrirDocumento } from '../utils/archivos';
import DocManager from './DocManager';
import BeneficiarioConfidencial from './BeneficiarioConfidencial';

import { fmtMoneda as fmt$ } from '../utils/moneda';
import { getAbrev } from '../utils/marca';

// Vista de detalle de póliza reutilizable, como CAPA (overlay). Se usa para ver una
// póliza sin salir de la pantalla donde estás (p. ej. desde la ficha del cliente):
// al cerrar vuelves exactamente a donde estabas, sin perder filtros.
export default function PolizaVistaModal({ polizaId, onClose }) {
  const abrev = getAbrev();
  const [tab, setTab] = useState('info');
  const [poliza, setPoliza] = useState(null);
  const [agentes, setAgentes] = useState([]);
  const [auto, setAuto] = useState(null);
  const [endosos, setEndosos] = useState([]);
  const [polizaAnterior, setPolizaAnterior] = useState(null);
  const [coberturas, setCoberturas] = useState([]);
  const [pagos, setPagos] = useState([]);

  const load = useCallback(() => {
    if (!polizaId) return;
    setTab('info');
    setPoliza(null);
    Promise.all([
      supabase.from('polizas')
        .select('*, clientes(nombre), aseguradoras(nombre), ramos(nombre), subramos(nombre), ejecutivos!polizas_ejecutivo_id_fkey(nombre)')
        .eq('id', polizaId).single(),
      supabase.from('poliza_agentes').select('porcentaje, agentes(nombre)').eq('poliza_id', polizaId),
      supabase.from('poliza_autos').select('*').eq('poliza_id', polizaId).maybeSingle(),
      supabase.from('poliza_endosos').select('*').eq('poliza_id', polizaId).order('fecha', { ascending: false }),
      supabase.from('poliza_coberturas').select('*').eq('poliza_id', polizaId),
      supabase.from('pagos').select('*').eq('poliza_id', polizaId).order('cuota_num').order('fecha_vencimiento'),
    ]).then(async ([{ data: p }, { data: a }, { data: v }, { data: e }, { data: cob }, { data: pg }]) => {
      setPoliza(p);
      setAgentes(a || []);
      setAuto(v || null);
      setEndosos(e || []);
      setCoberturas(cob || []);
      setPagos(pg || []);
      if (p?.poliza_anterior_id) {
        const { data: prev } = await supabase.from('polizas').select('numero').eq('id', p.poliza_anterior_id).maybeSingle();
        setPolizaAnterior(prev || null);
      } else {
        setPolizaAnterior(null);
      }
    });
  }, [polizaId]);

  useEffect(() => { load(); }, [load]);

  if (!polizaId) return null;

  const hoy = new Date().toISOString().slice(0, 10);
  const totalPlan = pagos.reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const pagadoPlan = pagos.filter(p => p.estado === 'pagado').reduce((s, p) => s + (parseFloat(p.monto) || 0), 0);
  const vencidasPlan = pagos.filter(p => p.estado === 'pendiente' && p.fecha_vencimiento && p.fecha_vencimiento < hoy).length;

  const TABS = [
    ['info', 'Información'],
    ['coberturas', `Coberturas${coberturas.length ? ` (${coberturas.length})` : ''}`],
    ['cobros', `Plan de Cobros${pagos.length ? ` (${pagos.length})` : ''}`],
    ['docs', 'Documentos'],
  ];

  return (
    <div className="modal-overlay" style={{ zIndex: 1500 }} onClick={onClose}>
      <div className="modal modal-lg" style={{ maxWidth: 720, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title" style={{ color:'var(--sky)' }}>{poliza?.numero || '...'}</div>
            <div className="text-silver text-sm">
              {poliza?.ramos?.nombre}{poliza?.subramos?.nombre ? ` / ${poliza.subramos.nombre}` : ''} · {poliza?.aseguradoras?.nombre}
            </div>
          </div>
          <button className="btn btn-icon btn-secondary btn-sm" onClick={onClose}><X size={16}/></button>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid var(--steel)', padding:'0 24px', flexShrink:0 }}>
          {TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} style={{
              padding:'10px 16px', background:'none', border:'none', cursor:'pointer',
              fontFamily:'var(--font)', fontSize:13, fontWeight:600,
              color: tab === key ? 'var(--sky)' : 'var(--silver)',
              borderBottom: tab === key ? '2px solid var(--sky)' : '2px solid transparent',
              marginBottom:-1,
            }}>{label}</button>
          ))}
        </div>

        {!poliza ? (
          <div className="modal-body" style={{ textAlign:'center', color:'var(--silver)', padding:40 }}>Cargando...</div>
        ) : (
          <div className="modal-body" style={{ overflowY:'auto', flex:1 }}>

            {/* ── Información ── */}
            {tab === 'info' && (<>
              <div className="form-grid form-grid-3" style={{ marginBottom:16 }}>
                {[
                  ['Cliente',       poliza.clientes?.nombre],
                  ['Estado',        poliza.estado],
                  ['Tipo',          poliza.tipo === 'renovacion' ? 'Renovación' : 'Nueva'],
                  ...(poliza.tipo === 'renovacion'
                    ? [['Póliza anterior', polizaAnterior?.numero || (poliza.poliza_anterior_id ? `#${poliza.poliza_anterior_id}` : '—')]]
                    : []),
                  ['Emisión p/ aseguradora', poliza.tipo_emision || poliza.tipo],
                  ['Prima neta',    poliza.prima ? fmt$(poliza.prima) : '—'],
                  ['Impuesto',      poliza.impuesto != null ? `${poliza.impuesto}%` : '—'],
                  ['Comisión ramo', poliza.comision_ramo != null ? `${poliza.comision_ramo}%` : '—'],
                  ...(poliza.sobrecomision_tipo
                    ? [['Sobrecomisión', poliza.sobrecomision_tipo === 'porcentaje' ? `${poliza.sobrecomision_valor}%` : fmt$(poliza.sobrecomision_valor)]]
                    : []),
                  ['Forma de pago', poliza.forma_pago || '—'],
                  ['Inicio',        fmtFecha(poliza.fecha_inicio) || '—'],
                  ['Vencimiento',   fmtFecha(poliza.fecha_vencimiento) || '—'],
                  ['Gastos expd.',  poliza.gastos_expedicion ? fmt$(poliza.gastos_expedicion) : '—'],
                  ['Participación', poliza.participacion != null ? `${poliza.participacion}%` : '—'],
                  ['Ejecutivo',     poliza.ejecutivos?.nombre || '—'],
                ].map(([l, v]) => (
                  <div key={l}>
                    <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                    <div style={{ fontSize:14, fontWeight:500 }}>{v || '—'}</div>
                  </div>
                ))}
              </div>

              {agentes.length > 0 && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:11, color:'var(--silver)', textTransform:'uppercase', marginBottom:8 }}>Agentes</div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {agentes.map((a, i) => (
                      <div key={i} style={{ padding:'6px 12px', background:'rgba(var(--sky-rgb),0.1)', borderRadius:8, fontSize:13 }}>
                        {a.agentes?.nombre} <span style={{ color:'var(--silver)' }}>{a.porcentaje}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {auto && (
                <div style={{ marginBottom:16 }}>
                  <div style={{ fontSize:11, color:'var(--silver)', textTransform:'uppercase', marginBottom:8 }}>Bien Asegurado — Vehículo</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                    {[
                      ['Placa',    auto.placa_por_reportar ? 'Por reportar' : (auto.placa || '—')],
                      ['Marca',    auto.marca],
                      ['Modelo',   auto.modelo],
                      ['Año',      auto.anio],
                      ['Color',    auto.color],
                      ['Tipo',     auto.tipo_vehiculo],
                      ['Chasis',   auto.chasis],
                      ['Motor',    auto.no_motor],
                      ['Uso',      auto.uso],
                    ].filter(([, v]) => v).map(([l, v]) => (
                      <div key={l} style={{ padding:'8px 12px', background:'rgba(var(--sky-rgb),0.08)', borderRadius:8, border:'1px solid rgba(var(--sky-rgb),0.15)' }}>
                        <div style={{ fontSize:10, color:'var(--silver)', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                        <div style={{ fontSize:13, fontWeight:600 }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(poliza.descripcion_riesgo || poliza.asegurado_nombre || poliza.beneficiario_nombre) && (
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {poliza.descripcion_riesgo && (
                    <div style={{ padding:'8px 12px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                      <div style={{ fontSize:10, color:'var(--silver)', marginBottom:2 }}>RIESGO</div>
                      <div style={{ fontSize:13 }}>{poliza.descripcion_riesgo}</div>
                    </div>
                  )}
                  {poliza.asegurado_nombre && (
                    <div style={{ padding:'8px 12px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                      <div style={{ fontSize:10, color:'var(--silver)', marginBottom:2 }}>ASEGURADO</div>
                      <div style={{ fontSize:13 }}>{poliza.asegurado_nombre} {poliza.asegurado_cedula ? `· ${poliza.asegurado_cedula}` : ''}</div>
                    </div>
                  )}
                  {poliza.beneficiario_nombre && (
                    <div style={{ padding:'8px 12px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                      <div style={{ fontSize:10, color:'var(--silver)', marginBottom:2 }}>BENEFICIARIO{poliza.beneficiario_oneroso ? ' (Oneroso)' : ''}</div>
                      <BeneficiarioConfidencial
                        nombre={`${poliza.beneficiario_nombre}${poliza.beneficiario_cedula ? ` · ${poliza.beneficiario_cedula}` : ''}`}
                        actualizadoEl={poliza.beneficiarios_actualizado_el}
                        nameStyle={{ fontSize:13 }} subStyle={{ fontSize:11, color:'var(--silver)' }} />
                    </div>
                  )}
                </div>
              )}

              {poliza.notas && (
                <div style={{ marginTop:12, padding:'8px 12px', background:'rgba(var(--steel-rgb),0.3)', borderRadius:8 }}>
                  <div style={{ fontSize:10, color:'var(--silver)', marginBottom:2 }}>NOTAS</div>
                  <div style={{ fontSize:13 }}>{poliza.notas}</div>
                </div>
              )}

              {endosos.length > 0 && (
                <div style={{ marginTop:16 }}>
                  <div style={{ fontSize:11, color:'var(--silver)', textTransform:'uppercase', marginBottom:8 }}>Historial de Endosos</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {endosos.map((en, i) => (
                      <div key={i} style={{ padding:'10px 14px', background:'rgba(245, 158, 11, 0.07)', borderRadius:8, border:'1px solid rgba(245, 158, 11, 0.2)' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                          <span style={{ fontSize:12, fontWeight:700 }}>{fmtFecha(en.fecha)}</span>
                          {en.delta_prima !== 0 && (
                            <span style={{ fontSize:12, fontWeight:700, color: en.delta_prima > 0 ? '#22c55e' : '#ef4444' }}>
                              {en.delta_prima > 0 ? '+' : ''}{fmt$(en.delta_prima)} prima
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize:13 }}>{en.descripcion}</div>
                        {en.url_documento && (
                          <a href="#" onClick={(e)=>{ e.preventDefault(); abrirDocumento(en.url_documento); }}
                            style={{ fontSize:11, color:'var(--sky-light)', marginTop:4, display:'inline-flex', alignItems:'center', gap:4 }}>
                            📎 {en.nombre_documento || 'Ver documento'}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>)}

            {/* ── Coberturas ── */}
            {tab === 'coberturas' && (
              coberturas.length === 0
                ? <div className="text-silver text-sm">Sin coberturas registradas.</div>
                : (
                  <div className="table-wrapper">
                    <table>
                      <thead><tr><th>Cobertura</th><th>Suma Asegurada</th><th>Tipo Límite</th><th>Deducible</th></tr></thead>
                      <tbody>
                        {coberturas.map((c, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight:600 }}>{c.nombre}</td>
                            <td>{c.suma_asegurada != null ? fmt$(c.suma_asegurada) : '—'}</td>
                            <td className="text-silver">{c.tipo_limite || '—'}</td>
                            <td className="text-silver">{c.deducible || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
            )}

            {/* ── Plan de Cobros ── */}
            {tab === 'cobros' && (
              pagos.length === 0
                ? <div className="text-silver text-sm">Sin plan de cobros.</div>
                : (<>
                  <div className="table-wrapper">
                    <table>
                      <thead><tr><th>Cuota</th><th>Monto</th><th>Vencimiento</th><th>Estado</th><th>Fecha pago</th><th>Recibo {abrev}</th></tr></thead>
                      <tbody>
                        {pagos.map((p, i) => {
                          const venc = p.fecha_vencimiento && p.fecha_vencimiento < hoy && p.estado === 'pendiente';
                          return (
                            <tr key={i} style={venc ? { background:'rgba(239, 68, 68, 0.06)' } : {}}>
                              <td style={{ color:'var(--silver)', fontSize:12 }}>{p.cuota_num ? `Cuota ${p.cuota_num}` : '—'}</td>
                              <td style={{ fontWeight:600, color:'var(--text-success)' }}>{fmt$(p.monto)}</td>
                              <td style={{ color: venc ? '#ef4444' : 'inherit' }}>{fmtFecha(p.fecha_vencimiento) || '—'}{venc && <span style={{ marginLeft:6, fontSize:10, color:'var(--text-danger)', fontWeight:700 }}>VENCIDA</span>}</td>
                              <td><span className={`badge ${p.estado==='pagado'?'badge-success':venc?'badge-danger':'badge-warning'}`}>{p.estado}</span></td>
                              <td style={{ fontSize:12, color:'var(--silver)' }}>{fmtFecha(p.fecha_pago) || '—'}</td>
                              <td style={{ fontSize:11, color:'var(--sky-light)', fontFamily:'monospace' }}>{p.num_recibo_ru || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{ display:'flex', gap:20, marginTop:14, padding:'10px 14px', background:'rgba(var(--navy-rgb),0.4)', borderRadius:8, fontSize:13 }}>
                    <span style={{ color:'var(--silver)' }}>Total: <strong style={{ color:'var(--text)' }}>{fmt$(totalPlan)}</strong></span>
                    <span style={{ color:'var(--silver)' }}>Pagado: <strong style={{ color:'var(--text-success)' }}>{fmt$(pagadoPlan)}</strong></span>
                    <span style={{ color:'var(--silver)' }}>Pendiente: <strong style={{ color: (totalPlan-pagadoPlan)>0?'#f59e0b':'#22c55e' }}>{fmt$(totalPlan - pagadoPlan)}</strong></span>
                    {vencidasPlan > 0 && <span style={{ color:'var(--text-danger)', fontWeight:700 }}>⚠️ {vencidasPlan} vencida(s)</span>}
                  </div>
                </>)
            )}

            {/* ── Documentos ── */}
            {tab === 'docs' && (
              <DocManager polizaId={polizaId} linkedClienteId={poliza.cliente_id}
                categoriasPermitidas={['solicitud','poliza','cliente_natural','cliente_juridico','otro']} readOnly />
            )}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
